import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Loader2, Percent, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import HelpButton from "@/components/HelpButton";

export const GROWTH_GROUPS = [["revenue", "Inkomsten", "revenue_breakdown"], ["opex", "Bedrijfskosten", "opex_breakdown"], ["other_income", "Overige inkomsten", "other_income_breakdown"]];
export const toPct = (v) => (v === "" || v === null || v === undefined ? "" : (Math.round(Number(v) * 1e6) / 1e4).toString());
export const fromPct = (val) => {
  if (val === "" || val === null || val === undefined) return null;
  const num = Number(String(val).replace(",", "."));
  return Number.isNaN(num) ? undefined : Math.round((num / 100) * 1e6) / 1e6;
};
export const countRates = (cfg) => Object.values(cfg || {}).reduce((n, cat) => n + Object.keys(cat || {}).length, 0);
// Laatste boekjaar met subposten → bepaalt welke posten getoond worden.
export const latestWithSubposts = (historicals) =>
  [...(historicals || [])].sort((a, b) => (b.year || 0) - (a.year || 0)).find((h) => GROWTH_GROUPS.some(([, , key]) => (h[key] || []).length > 0)) || null;

// Clubpagina › Scenario's: standaard inflatie per subpost die elk nieuw scenario overneemt.
export default function ClubSubpostGrowthCard({ t, clubId, club, historicals, canEdit, onSaved }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState({});
  const [saving, setSaving] = useState(false);
  const last = useMemo(() => latestWithSubposts(historicals), [historicals]);
  useEffect(() => { setCfg(club?.default_subpost_growth || {}); }, [club?.default_subpost_growth]);
  if (!last) return null;

  const set = (cat, name, val) => {
    const parsed = fromPct(val);
    if (parsed === undefined) return;
    const catObj = { ...(cfg[cat] || {}) };
    if (parsed === null) delete catObj[name]; else catObj[name] = parsed;
    setCfg({ ...cfg, [cat]: catObj });
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/clubs/${clubId}/subpost-growth`, { subpost_growth: cfg });
      toast.success(t("Club-standaard opgeslagen ({n} posten). Nieuwe scenario's starten hiermee.").replace("{n}", countRates(data.default_subpost_growth)));
      onSaved?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const n = countRates(cfg);

  return (
    <div className="col-span-full bg-card border-2 border-foreground heavy-shadow" data-testid="club-subpost-growth-card">
      <div className="p-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="stat-label">{t("Club-standaard")}</div>
          <h3 className="font-display font-black text-xl uppercase tracking-tight inline-flex items-center gap-2"><Percent className="w-4 h-4" /> {t("Standaard inflatie per subpost")}</h3>
          <p className="text-sm text-muted-foreground mt-1" data-testid="club-subpost-growth-summary">
            {n > 0 ? t("{n} posten met een standaardpercentage — elk nieuw scenario start hiermee (per scenario aan te passen).").replace("{n}", n)
              : t("Nog geen standaard ingesteld: nieuwe scenario's volgen de categoriegroei. Posten: boekjaar {y}.").replace("{y}", last.year)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton section="inflatie-per-subpost" label="Uitleg inflatie per subpost" size="sm" />
          <button onClick={() => setOpen(!open)} data-testid="club-subpost-growth-toggle" className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5">
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} {open ? t("Inklappen") : t("Bewerken")}
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t-2 border-foreground p-5">
          <div className="grid md:grid-cols-3 gap-6">
            {GROWTH_GROUPS.map(([cat, label, key]) => (
              <div key={cat}>
                <div className="font-display uppercase font-bold text-xs tracking-widest mb-2">{t(label)}</div>
                {(last[key] || []).length === 0 && <div className="text-xs text-muted-foreground">{t("Geen subposten")}</div>}
                {(last[key] || []).map((it) => (
                  <div key={it.name} className="flex items-center gap-2 py-1">
                    <span className="flex-1 truncate text-sm font-mono-num" title={it.name}>{it.name}</span>
                    <div className="relative">
                      <input type="number" step="any" placeholder="—" disabled={!canEdit} value={toPct(cfg[cat]?.[it.name])} onChange={(e) => set(cat, it.name, e.target.value)}
                        data-testid={`club-sp-${cat}-${it.name}`}
                        className="w-24 border-2 border-foreground bg-background px-2 py-1 pr-6 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground font-mono-num text-xs pointer-events-none">%</span>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
            <p className="text-xs text-muted-foreground">{t("Leeg = geen standaard: de post volgt dan de categoriegroei van het scenario. Bestaande scenario's veranderen niet.")}</p>
            {canEdit && (
              <button onClick={save} disabled={saving} data-testid="club-subpost-growth-save" className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {t("Opslaan als club-standaard")}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
